import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { statusColor, priorityColor, humanTime } from "@/lib/utils";
import { Headset, PaperPlaneTilt, BookOpen, SignOut, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ThemeToggle } from "@/components/ThemeToggle";
import { TrakLogo } from "@/components/TrakLogo";

export default function EndUserPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [myTickets, setMyTickets] = useState([]);
  const [kb, setKb] = useState([]);
  const [npsScore, setNpsScore] = useState(null);
  const [npsDone, setNpsDone] = useState(false);
  const [npsDismissed, setNpsDismissed] = useState(false);
  const [categories, setCategories] = useState(["General"]);
  const [form, setForm] = useState({ title:"", description:"", priority:"P3", category:"General", customer_name: user?.name || "", email: user?.email || "", cc_email: "" });
  const load = () => {
    api.get("/incidents").then(r => setMyTickets(r.data.filter(t => t.requester_email === user?.email))).catch(() => {});
    api.get("/kb").then(r => setKb(r.data)).catch(() => {});
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);
  useEffect(() => { if (user) setForm(f => ({ ...f, email: f.email || user.email, customer_name: f.customer_name || user.name })); }, [user]);
  const submit = async () => {
    if (!form.title) return;
    const email = (form.email || user.email || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Please enter a valid email address"); return; }
    const cc = (form.cc_email || "").trim();
    if (cc) {
      const parts = cc.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
      const bad = parts.find(p => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(p));
      if (bad) { toast.error(`Invalid CC email: ${bad}`); return; }
    }
    await api.post("/incidents", { title: form.title, description: form.description, priority: form.priority, category: form.category, customer_name: form.customer_name, requester_email: email, cc_email: cc, source: "portal" });
    toast.success("Ticket submitted");
    setForm({ title:"", description:"", priority:"P3", category:"General", customer_name: user?.name || "", email: user?.email || "", cc_email: "" });
    load();
  };
  const submitNps = async () => {
    if (npsScore === null) return;
    try {
      await api.post("/public/nps", { score: npsScore, email: user.email });
      setNpsDone(true);
    } catch { toast.error("Could not submit"); }
  };
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="portal-view">
      <header className="h-16 border-b flex items-center px-6 bg-sidebar text-sidebar-foreground border-sidebar-border dark:bg-background dark:text-foreground dark:border-border">
        <div className="h-8 w-8 grid place-items-center bg-white text-sidebar dark:bg-primary dark:text-primary-foreground rounded-sm mr-2">
          <TrakLogo size={20} />
        </div>
        <div>
          <div className="font-display font-bold">TRAK Self-Service</div>
          <div className="text-[11px] font-mono text-sidebar-muted dark:text-muted-foreground">Agent workspace? <Link to="/" data-testid="portal-to-agent" className="text-white hover:opacity-80 dark:text-primary hover:underline">Open here</Link></div>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <ThemeToggle />
          <div className="text-xs text-right"><div>{user?.name}</div><div className="font-mono text-sidebar-muted dark:text-muted-foreground">{user?.email}</div></div>
          <button data-testid="portal-logout" onClick={async ()=>{ await logout(); nav("/login", { replace: true }); }} className="text-sidebar-muted hover:text-white dark:text-muted-foreground dark:hover:text-primary"><SignOut size={18}/></button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 border-border rounded-sm lg:col-span-2">
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">New Request</div>
          <h2 className="font-display text-2xl mb-4">Submit a ticket</h2>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase">Client</Label><Input data-testid="portal-customer" className="mt-1 rounded-sm" value={form.customer_name} onChange={(e)=>setForm({...form,customer_name:e.target.value})} /></div>
            <div><Label className="text-xs uppercase">Email</Label><Input type="email" data-testid="portal-email" className="mt-1 rounded-sm" placeholder="you@company.com" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} /></div>
            <div><Label className="text-xs uppercase">CC emails <span className="text-muted-foreground normal-case">(optional, comma-separated)</span></Label><Input data-testid="portal-cc-email" className="mt-1 rounded-sm" placeholder="colleague@company.com, manager@company.com" value={form.cc_email} onChange={(e)=>setForm({...form,cc_email:e.target.value})} /></div>
            <div><Label className="text-xs uppercase">Subject</Label><Input data-testid="portal-title" className="mt-1 rounded-sm" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} /></div>
            <div><Label className="text-xs uppercase">Describe the issue</Label><Textarea data-testid="portal-desc" className="mt-1 rounded-sm min-h-[130px]" value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase">Priority</Label><Select value={form.priority} onValueChange={(v)=>setForm({...form,priority:v})}><SelectTrigger data-testid="portal-priority" className="mt-1 rounded-sm"><SelectValue/></SelectTrigger><SelectContent>{["P1","P2","P3","P4"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs uppercase">Category</Label><Select value={form.category} onValueChange={(v)=>setForm({...form,category:v})}><SelectTrigger data-testid="portal-category" className="mt-1 rounded-sm"><SelectValue/></SelectTrigger><SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <Button data-testid="portal-submit" onClick={submit} disabled={!form.title} className="w-full rounded-sm font-mono uppercase text-xs"><PaperPlaneTilt size={14} className="mr-2"/>Submit</Button>
          </div>
        </Card>
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Help Center</div>
          <h2 className="font-display text-xl mb-3">Popular articles</h2>
          <div className="space-y-2">
            {kb.slice(0,5).map(a => (
              <div key={a.id} className="p-3 border border-border rounded-sm hover:border-primary/60">
                <div className="flex items-center gap-2 text-sm"><BookOpen size={12} className="text-primary" /> {a.title}</div>
                <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{a.body}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <div className="max-w-6xl mx-auto px-6 pb-10">
        {!npsDismissed && (
          <Card className="border-primary/40 bg-primary/5 rounded-sm p-5 mb-6" data-testid="nps-card">
            {npsDone ? (
              <div className="text-sm text-center text-primary font-mono">Thanks for your feedback! 🎉</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-medium">How likely are you to recommend TRAK support to a colleague?</div>
                  <button data-testid="nps-dismiss" onClick={()=>setNpsDismissed(true)} className="text-xs font-mono text-muted-foreground hover:text-foreground">dismiss</button>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {Array.from({length: 11}, (_, n) => (
                    <button
                      key={n}
                      data-testid={`nps-score-${n}`}
                      onClick={()=>setNpsScore(n)}
                      className={`h-9 w-9 rounded-sm border text-sm font-mono transition-colors ${npsScore === n ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:border-primary/60'}`}
                    >{n}</button>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">0 = Not likely · 10 = Extremely likely</div>
                  <Button data-testid="nps-submit" onClick={submitNps} disabled={npsScore === null} size="sm" className="rounded-sm font-mono uppercase text-xs">Submit</Button>
                </div>
              </>
            )}
          </Card>
        )}
        <Card className="border-border rounded-sm overflow-hidden">
          <div className="p-4 border-b border-border"><div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">My Tickets</div><div className="font-display text-lg">{myTickets.length} total</div></div>
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border"><th className="text-left px-4 py-3">Ticket</th><th className="text-left px-4 py-3">Subject</th><th className="text-left px-4 py-3">Priority</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Updated</th><th className="text-left px-4 py-3">Feedback</th></tr></thead>
            <tbody>
              {myTickets.map(t => {
                const resolved = t.status === 'resolved' || t.status === 'closed';
                return (
                <tr key={t.id} className="border-b border-border/60">
                  <td className="px-4 py-3 font-mono text-xs text-primary">{t.number}</td>
                  <td className="px-4 py-3">{t.title}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 border ${priorityColor(t.priority)}`}>{t.priority}</span></td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 border uppercase ${statusColor(t.status)}`}>{t.status.replace('_',' ')}</span></td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{humanTime(t.updated_at)}</td>
                  <td className="px-4 py-3">
                    {resolved ? (
                      <Link data-testid={`rate-ticket-${t.number}`} to={`/feedback/${t.id}`} className="inline-flex items-center gap-1 text-xs font-mono uppercase text-primary hover:underline"><Star size={12} weight="fill" /> Rate</Link>
                    ) : <span className="text-[10px] font-mono text-muted-foreground">—</span>}
                  </td>
                </tr>
                );
              })}
              {myTickets.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No tickets yet — submit one above.</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
